import React from 'react';
import { Chart } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { getMobileDualAxisLayoutPadding, getResponsiveXAxisTicks, isMobileChartViewport } from '../../utils/chartResponsive';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';

interface SalesTrendData {
  month: string;
  sales: number;
  invoices: number;
}

interface CombinedChartProps {
  data: SalesTrendData[];
  currencySymbol: string;
  formatKpiValue: (value: number | string, type?: 'currency' | 'percentage' | 'number') => string;
}

const CombinedChart: React.FC<CombinedChartProps> = ({
  data,
  currencySymbol,
  formatKpiValue,
}) => {
  const { currentTheme } = useTheme();

  return (
    <>
      <div className="mobile-chart-host" style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار ترکیبی روند مبلغ فروش و تعداد سفارش">
        <Chart
          type='bar'
          data={{
            labels: data.map(item => item.month || ''),
            datasets: [
              {
                type: 'line' as const,
                label: 'مبلغ فروش',
                data: data.map(item => item.sales || 0),
                borderColor: getChartColors(currentTheme)[0],
                backgroundColor: getChartBackgroundColor(0, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[0],
                tension: 0.4,
                fill: true,
                yAxisID: 'y',
                order: 1,
              },
              {
                type: 'bar' as const,
                label: 'تعداد سفارش',
                data: data.map(item => item.invoices || 0),
                backgroundColor: getChartBackgroundColor(1, currentTheme, 0.8),
                borderColor: getChartColors(currentTheme)[1],
                borderWidth: 2,
                borderRadius: 6,
                barThickness: isMobileChartViewport() ? 16 : 30,
                yAxisID: 'y1',
                order: 2,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            layout: isMobileChartViewport()
              ? { padding: getMobileDualAxisLayoutPadding() }
              : undefined,
            interaction: {
              mode: 'index',
              intersect: false,
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                display: true,
                position: 'bottom',
                align: 'end',
                labels: {
                  color: 'var(--chart-legend-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.legend.size,
                    weight: 500,
                  },
                  padding: 20,
                  usePointStyle: true,
                  pointStyle: 'circle',
                }
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const datasetLabel = context.dataset.label;
                    const separator = '\u00A0:\u00A0';
                    
                    if (datasetLabel === 'مبلغ فروش') {
                      const value = `${formatKpiValue(context.parsed.y, 'currency')} ${currencySymbol}`;
                      return `${datasetLabel}${separator}${value}`;
                    } else {
                      const value = `${formatKpiValue(context.parsed.y)} سفارش`;
                      return `${datasetLabel}${separator}${value}`;
                    }
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                title: {
                  display: true,
                  text: 'ماه',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                type: 'linear',
                display: true,
                position: 'right',
                beginAtZero: true,
                afterFit: function(scaleInstance: any) {
                  scaleInstance.width = Math.max(scaleInstance.width, isMobileChartViewport() ? 68 : 96);
                },
                grid: {
                  color: 'var(--chart-grid-color)',
                },
                ticks: {
                  color: 'var(--chart-axis-color)',
                  padding: 8,
                  font: {
                    family: 'var(--font-primary)',
                    size: isMobileChartViewport() ? 9 : 13,
                  },
                  callback: function(value: any) {
                    const num = Number(value);
                    if (Math.abs(num) >= 1_000_000_000) {
                      return `${formatKpiValue(num / 1_000_000_000)} میلیارد`;
                    }
                    if (Math.abs(num) >= 1_000_000) {
                      return `${formatKpiValue(num / 1_000_000)} میلیون`;
                    }
                    return formatKpiValue(num, 'currency');
                  },
                },
                title: {
                  display: false,
                  text: `مبلغ فروش (${currencySymbol})`,
                },
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                afterFit: function(scaleInstance: any) {
                  scaleInstance.width = Math.max(scaleInstance.width, isMobileChartViewport() ? 52 : 72);
                },
                grid: {
                  drawOnChartArea: false,
                },
                ticks: {
                  color: 'var(--chart-axis-color)',
                  padding: 8,
                  font: {
                    family: 'var(--font-primary)',
                    size: isMobileChartViewport() ? 9 : 13,
                  },
                  callback: function(value: any) {
                    return formatKpiValue(value);
                  },
                },
                title: {
                  display: false,
                  text: 'تعداد سفارش',
                },
              },
            }
          }}
        />
      </div>
      
      {/* Combined Summary Stats */}
      {data.length > 0 && (
        <div className="mt-4 p-4 bg-bg-secondary rounded-lg">
          <div className="sales-trend-stats-grid grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-text-tertiary mb-1">میانگین فروش ماهیانه</div>
              <div className="font-semibold text-text-primary">
                {formatKpiValue(
                  data.reduce((sum, item) => sum + (item.sales || 0), 0) / data.length,
                  'currency'
                )} {currencySymbol}
              </div>
            </div>
            <div>
              <div className="text-text-tertiary mb-1">آخرین فروش</div>
              <div className="font-semibold text-text-primary">
                {formatKpiValue(data[data.length - 1]?.sales || 0, 'currency')} {currencySymbol}
              </div>
            </div>
            <div>
              <div className="text-text-tertiary mb-1">میانگین سفارش ماهیانه</div>
              <div className="font-semibold text-text-primary">
                {formatKpiValue(
                  Math.round(data.reduce((sum, item) => sum + (item.invoices || 0), 0) / data.length)
                )} سفارش
              </div>
            </div>
            <div>
              <div className="text-text-tertiary mb-1">آخرین سفارش</div>
              <div className="font-semibold text-text-primary">
                {formatKpiValue(data[data.length - 1]?.invoices || 0)} سفارش
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CombinedChart;